/** Shared helpers for INT-FE-005–007 Ask-mode intelligence UI. */

import {
    hasAccuracyTrustData,
    resolveAccuracyModeStatus,
    resolveIntelligenceAbstention,
    shouldShowAccuracyTrustStrip,
} from './accuracyModeMeta';

export function normalizeObservationCandidates(raw) {
    const list = Array.isArray(raw) ? raw.filter(Boolean) : [];
    return [...list].sort((a, b) => {
        const pa = a?.consultant_priority;
        const pb = b?.consultant_priority;
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return Number(pb) - Number(pa);
    });
}

export function extractGroundingFields(evidenceGovernance, retrievalDebug) {
    const gov = evidenceGovernance && typeof evidenceGovernance === 'object' ? evidenceGovernance : {};
    const debug = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};

    return {
        grounding_status: gov.grounding_status ?? debug.grounding_status ?? null,
        citation_coverage: gov.citation_coverage ?? debug.citation_coverage ?? debug.final_citation_coverage ?? null,
        unsupported_claim_count: gov.unsupported_claim_count ?? debug.unsupported_claim_count ?? null,
        warnings: Array.isArray(gov.warnings)
            ? gov.warnings
            : Array.isArray(debug.evidence_governance_warnings)
              ? debug.evidence_governance_warnings
              : [],
        hallucination_risk_flag: Boolean(debug.hallucination_risk_flag),
    };
}

export function groundingDisplayTone({ grounding_status, unsupported_claim_count, hallucination_risk_flag }) {
    const status = String(grounding_status || '')
        .toLowerCase()
        .trim();

    if (['strongly_grounded', 'ok', 'grounded'].includes(status)) return 'success';
    if (['partially_grounded', 'weakly_grounded', 'partial', 'weak_coverage'].includes(status)) return 'warning';
    if (['unsupported_claims', 'hallucination_risk'].includes(status) || hallucination_risk_flag) return 'danger';
    if (status === 'blocked_no_evidence') return 'neutral';
    if (Number(unsupported_claim_count) > 0) return 'warning';
    return 'neutral';
}

export function formatGroundingStatusLabel(grounding_status) {
    if (!grounding_status) return 'Grounding';
    return String(grounding_status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

export function formatCitationCoveragePercent(citation_coverage) {
    if (citation_coverage == null || citation_coverage === '') return null;
    const n = Number(citation_coverage);
    if (!Number.isFinite(n)) return null;
    const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
    return `${pct}%`;
}

export function resolveAskIntelligenceContext(askResults, lastAsk) {
    if (!askResults || askResults.mode !== 'single') return null;

    const data = askResults.data || {};
    const meta = askResults.intelligenceMeta || {};
    const stored = lastAsk?.intelligence_source ? lastAsk : null;

    const evidence_governance = meta.evidence_governance ?? data.evidence_governance ?? stored?.evidence_governance ?? null;
    const retrieval_debug = meta.retrieval_debug ?? data.retrieval_debug ?? stored?.retrieval_debug ?? {};
    const observation_candidates = normalizeObservationCandidates(
        meta.observation_candidates ?? data.observation_candidates ?? stored?.observation_candidates,
    );
    const evidence = data.evidence?.length ? data.evidence : stored?.evidence || [];
    const citations = data.citations?.length ? data.citations : stored?.citations || [];

    const grounding = extractGroundingFields(evidence_governance, retrieval_debug);
    const hasGrounding =
        Boolean(grounding.grounding_status) ||
        grounding.citation_coverage != null ||
        Number(grounding.unsupported_claim_count) > 0 ||
        grounding.warnings.length > 0 ||
        grounding.hallucination_risk_flag;

    const hasFilename =
        Boolean(retrieval_debug?.filename_match_detected) ||
        (Array.isArray(retrieval_debug?.resolved_file_ids) && retrieval_debug.resolved_file_ids.length > 0) ||
        retrieval_debug?.filename_resolution_status === 'semantic_fallback' ||
        Boolean(retrieval_debug?.filename_fallback_semantic);

    const showIntelligenceUi =
        Boolean(askResults.usedIntelligenceAsk) || Boolean(data.intelligence_source) || hasGrounding || observation_candidates.length > 0;

    const abstention = resolveIntelligenceAbstention({
        answer: data.final_answer || data.answer,
        retrievalDebug: retrieval_debug,
        evidenceGovernance: evidence_governance,
    });
    const accuracyModeStatus = resolveAccuracyModeStatus(retrieval_debug);
    const accuracyModeRequested = accuracyModeStatus.requested;
    const accuracyModeActive = accuracyModeStatus.active;
    const userAccuracyModeOn = askResults.accuracyMode === true;
    const hasAccuracyTrust = shouldShowAccuracyTrustStrip({
        accuracyModeEnabled: userAccuracyModeOn,
        retrievalDebug: retrieval_debug,
    });

    return {
        evidence_governance,
        retrieval_debug,
        observation_candidates,
        evidence,
        citations,
        grounding,
        hasGrounding,
        hasFilename,
        showIntelligenceUi,
        abstention,
        hasAccuracyTrust,
        accuracyModeRequested,
        accuracyModeActive,
        accuracyModeInactiveReason: accuracyModeStatus.inactiveReason,
        accuracyPipelineRan: accuracyModeStatus.pipelineRan,
        accuracyModeStatus,
    };
}

export function resolveFilenameDisplayName({ retrieval_debug, evidence, citations, resolvedFileId }) {
    const fid = resolvedFileId != null ? String(resolvedFileId) : null;
    if (fid) {
        for (const ev of evidence || []) {
            const meta = ev?.metadata || {};
            if (String(meta.file_id) === fid) {
                return meta.metadata_storage_name || meta.document_name || null;
            }
        }
        for (const c of citations || []) {
            if (String(c.file_id ?? c.referenced_file_id) === fid) {
                return c.document_name || null;
            }
        }
    }
    const candidates = retrieval_debug?.filename_candidates;
    if (Array.isArray(candidates) && candidates[0]) return String(candidates[0]);
    return null;
}

export function resolveFilenameBannerMessage(ctx) {
    const debug = ctx?.retrieval_debug;
    if (!debug || typeof debug !== 'object') return null;

    const resolvedIds = Array.isArray(debug.resolved_file_ids) ? debug.resolved_file_ids : [];
    const detected = Boolean(debug.filename_match_detected);
    const semantic =
        debug.filename_resolution_status === 'semantic_fallback' || Boolean(debug.filename_fallback_semantic);

    if (resolvedIds.length > 0) {
        const name = resolveFilenameDisplayName({
            retrieval_debug: debug,
            evidence: ctx.evidence,
            citations: ctx.citations,
            resolvedFileId: resolvedIds[0],
        });
        return {
            tone: 'info',
            message: name ? `Matched file: ${name}` : `Matched file (id ${resolvedIds[0]})`,
        };
    }

    if (detected) {
        return {
            tone: 'warning',
            message: 'Filename mentioned but no accessible exact match found',
        };
    }

    if (semantic) {
        return { tone: 'info', message: 'Searched semantically across permitted files' };
    }

    return null;
}

export function formatObservationRef(ref) {
    if (ref == null || ref === '') return null;
    if (typeof ref === 'number' && Number.isFinite(ref)) return `[${ref}]`;
    const s = String(ref).trim();
    if (/^\[\d+\]$/.test(s)) return s;
    if (/^\d+$/.test(s)) return `[${s}]`;
    return s;
}
